import React from 'react';
import { Form, Switch, Input, Card, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

interface MessageSettingsProps {
  form: any;
}

const MessageSettings: React.FC<MessageSettingsProps> = () => {
  const { t } = useTranslation();
  
  return (
    <Card title={t('settings.messageSettings.title')} variant="outlined">
      <Row gutter={[16, 16]}>
        {/* Approved Load Messages */}
        <Col xs={24} md={12}>
          <Card title={t('settings.messageSettings.loadMessages')} variant="outlined">
            <Form.Item
              name="useMessageFlow"
              label={t('settings.messageSettings.userFlow')}
              valuePropName="checked"
              tooltip={t('settings.messageSettings.customizeMessages')}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate
            >
              {({ getFieldValue }) =>
                getFieldValue('useMessageFlow') ? (
                  <Form.Item
                    name="messageFlowId"
                    label={t('settings.messageSettings.messageID')}
                    rules={[{ required: true, message: 'Please enter a flow ID' }]}
                  >
                    <TextArea rows={4} placeholder={t('settings.messageSettings.messageID')} />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="textMessage"
                    label={t('settings.messageSettings.customizeMessages')}
                    rules={[{ required: true, message: 'Please enter a message' }]}
                  >
                    <TextArea 
                      rows={4} 
                      placeholder={t('settings.messageSettings.availableVariables')} 
                    />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Card>
        </Col>

        {/* Rejected Load Messages */}
        <Col xs={24} md={12}>
          <Card title={t('settings.requestSettings.duplicatedReceipt')} variant="outlined">
            <Form.Item
              name="useLoadRejectedFlow"
              label={t('settings.messageSettings.userFlow')}
              valuePropName="checked"
              tooltip={t('settings.messageSettings.customizeMessages')}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate
            >
              {({ getFieldValue }) =>
                getFieldValue('useLoadRejectedFlow') ? (
                  <Form.Item
                    name="loadRejectedFlowId"
                    label={t('settings.messageSettings.messageID')}
                    rules={[{ required: true, message: 'Please enter a flow ID' }]}
                  >
                    <TextArea rows={4} placeholder={t('settings.messageSettings.messageID')} />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="loadRejectedMessage"
                    label={t('settings.requestSettings.generalLoad')}
                    rules={[{ required: true, message: 'Please enter a message' }]}
                  >
                    <TextArea 
                      rows={4} 
                      placeholder={t('settings.messageSettings.availableVariables')} 
                    />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Card>
        </Col>

        {/* Withdraw Messages */}
        <Col xs={24} md={12}>
          <Card title={t('settings.messageSettings.withdrawMessages')} variant="outlined">
            <Form.Item
              name="useWithdrawFlow"
              label={t('settings.messageSettings.useWithdrawFlow')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate
            >
              {({ getFieldValue }) =>
                getFieldValue('useWithdrawFlow') ? (
                  <Form.Item
                    name="withdrawFlowId"
                    label={t('settings.messageSettings.withdrawID')}
                    rules={[{ required: true }]}
                  >
                    <TextArea rows={4} />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="withdrawMessage"
                    label={t('settings.withdrawSettings.withdrawMessage')}
                    rules={[{ required: true }]}
                  >
                    <TextArea rows={4} />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Card>
        </Col>
      </Row>

      {/* Rollover Messages */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Mensajes de Rollover" variant="outlined">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverBonusReceivedMessage"
                  label="Bonus recibido"
                  tooltip="Variables: {bonus_amount}, {requisito}, {username}"
                >
                  <TextArea rows={3} placeholder="Te acreditamos ${bonus_amount} de bonus! Para retirar necesitas acumular ${requisito} en apuestas." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverDepositPendingMessage"
                  label="Deposito con rollover pendiente"
                  tooltip="Variables: {faltante}, {username}"
                >
                  <TextArea rows={3} placeholder="Carga acreditada. Tenes un bonus pendiente de liberar. Aposta {faltante} mas para desbloquearlo." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverDepositFulfilledMessage"
                  label="Rollover cumplido en deposito"
                  tooltip="Variables: {bonus_amount}, {nuevo_bonus}, {username}"
                >
                  <TextArea rows={3} placeholder="Tu bonus anterior de ${bonus_amount} fue liberado! Te acreditamos ${nuevo_bonus} de bonus." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverWithdrawCaminoBMessage"
                  label="Retiro - Camino B"
                  tooltip="Variables: {faltante}, {retirable}, {bonus_amount}, {username}"
                >
                  <TextArea rows={3} placeholder="No cumpliste las condiciones del bonus. Podes retirar {retirable} pero perdes el bonus de {bonus_amount}." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverWithdrawNoBalanceMessage"
                  label="Retiro sin saldo suficiente"
                  tooltip="Variables: {bonus_amount}, {username}"
                >
                  <TextArea rows={3} placeholder="Tu saldo actual no supera el bonus de ${bonus_amount}. No hay monto disponible para retirar." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverWithdrawFulfilledMessage"
                  label="Retiro con rollover cumplido"
                  tooltip="Variables: {username}"
                >
                  <TextArea rows={3} placeholder="Tu bonus fue liberado. Retiro en proceso." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverCaminoBAcceptedMessage"
                  label="Camino B aceptado"
                  tooltip="Variables: {retirable}, {bonus_amount}, {username}"
                >
                  <TextArea rows={3} placeholder="Retiro de ${retirable} en proceso. El bonus de ${bonus_amount} fue cancelado." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="rolloverScrapingFailedMessage"
                  label="Error de verificacion"
                  tooltip="Variables: {username}"
                >
                  <TextArea rows={3} placeholder="Tu solicitud esta siendo revisada manualmente, te responderemos a la brevedad." />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default MessageSettings;